import * as React from "react";
import { cn } from "../lib/utils";

export type OtpCharset = "numeric" | "alphanumeric";

export interface OtpInputProps {
  length?: number;
  charset?: OtpCharset;
  value: string;
  onChange: (next: string) => void;
  onComplete?: (full: string) => void;
  error?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}

export function OtpInput({
  length = 6,
  charset = "numeric",
  value,
  onChange,
  onComplete,
  error,
  autoFocus,
  disabled,
}: OtpInputProps) {
  const isAlnum = charset === "alphanumeric";
  const stripRegex = isAlnum ? /[^A-Z0-9]/g : /\D/g;
  const normalize = (s: string) =>
    (isAlnum ? s.toUpperCase() : s).replace(stripRegex, "");
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);

  React.useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setDigit = (idx: number, digit: string) => {
    const chars = value.padEnd(length, " ").split("");
    chars[idx] = digit || " ";
    const next = chars.join("").replace(/ /g, "").slice(0, length);
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };

  const focusIdx = (idx: number) => {
    const i = Math.max(0, Math.min(length - 1, idx));
    refs.current[i]?.focus();
    refs.current[i]?.select();
  };

  const handleChange = (idx: number, raw: string) => {
    const digit = normalize(raw).slice(-1);
    setDigit(idx, digit);
    if (digit) focusIdx(idx + 1);
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const chars = value.split("");
      if (!chars[idx] && idx > 0) {
        focusIdx(idx - 1);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowLeft") {
      focusIdx(idx - 1);
      e.preventDefault();
    }
    if (e.key === "ArrowRight") {
      focusIdx(idx + 1);
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = normalize(e.clipboardData.getData("text")).slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    if (text.length === length) onComplete?.(text);
    else focusIdx(text.length);
  };

  return (
    <div className="flex gap-2 justify-center my-1">
      {Array.from({ length }).map((_, i) => {
        const digit = value[i] ?? "";
        const filled = digit !== "";
        return (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode={isAlnum ? "text" : "numeric"}
            pattern={isAlnum ? "[A-Za-z0-9]" : "[0-9]"}
            autoCapitalize={isAlnum ? "characters" : undefined}
            autoComplete="off"
            maxLength={1}
            value={digit}
            disabled={disabled}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={cn(
              isAlnum
                ? "w-9 h-12 text-[18px]"
                : "w-12 h-14 text-[22px]",
              "text-center font-semibold rounded-base border-[1.5px] bg-surface text-ink transition-colors uppercase",
              "focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgb(43_111_216/0.08)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
              error && "border-danger bg-danger-soft",
              !error && filled && "border-primary-border bg-primary-soft",
              !error && !filled && "border-border-strong",
            )}
          />
        );
      })}
    </div>
  );
}
